import { HttpParams } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { dateFormat } from '@carm/shared/constants/const-carm';
import { KeyValue } from '@carm/_core/metadata/models';
import { BaseApiService } from '@carm/_core/baseapi.service';
import { MetadataService } from '@carm/_core/metadata/services';
import * as dayjs from 'dayjs';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LoaderSpinnerTypes } from '@carm/_core';
import { IGetLineAdjustments, SoaCategory } from '../models/soa-list.model';
import { ISoaLineAdjustmentFilter, ISoaRequest, ISoaValidationResponse, ISoaSubmitResponse, ISoaPaymentConfirmationRequest, ISoaPaymentConfirmationResponse, ISoaResponse } from '../models/api/soa-api.model';
import { formatLineAdjustments } from '../models/soa.mapper';

@Injectable()
export class SoaService extends BaseApiService {
  constructor(
    protected injector: Injector,
    private metadataService: MetadataService) {
    super(injector);
  }

  get baseSoaUrlBP(): string {
    return `${this.endPointBP}/statement-of-adjustment`;
  }

  public getLineAdjustmentsList(
    searchString: string,
    minDate: Date,
    maxDate: Date,
    sortField: string,
    currentPage: number,
    pageSize: number,
  ): Observable<IGetLineAdjustments> {
    const endPoint = `${this.baseSoaUrlBP}/list`;
    const params = this.getHttpPameter(
      searchString,
      minDate,
      maxDate,
      sortField,
      currentPage,
      pageSize,
    );

    return this.httpAuthGet<IGetLineAdjustments>(endPoint, params, LoaderSpinnerTypes.AgGrid).pipe(
      map(data => formatLineAdjustments(data)),
    );
  }

  public getFilteredLineAdjustmentsList(
    searchString: string,
    minDate: Date,
    maxDate: Date,
    sortField: string,
    currentPage: number,
    pageSize: number,
    selectedCads: ISoaLineAdjustmentFilter,
  ): Observable<IGetLineAdjustments> {
    const params = this.getHttpPameter(
      searchString,
      minDate,
      maxDate,
      sortField,
      currentPage,
      pageSize,
    );
    const endPoint = `${this.baseSoaUrlBP}/list?${params.toString()}`;

    return this.httpAuthPost<IGetLineAdjustments>(endPoint, selectedCads).pipe(
      map(data => formatLineAdjustments(data)),
    );
  }

  private getHttpPameter(
    searchString: string,
    minDate: Date,
    maxDate: Date,
    sortField: string,
    currentPage: number,
    pageSize: number,
  ): HttpParams {
    const params = new HttpParams()
      .set('currentPage', currentPage.toString())
      .set('pageSize', pageSize.toString())
      .set('startDate', dayjs(minDate).format(dateFormat))
      .set('endDate', dayjs(maxDate).format(dateFormat))
      .set('sortField', sortField)
      .set('searchString', searchString || '');
    return params;
  }

  public getSoaReasons(type: SoaCategory): Observable<KeyValue[]> {
    switch (type) {
      case SoaCategory.Trade:
      case SoaCategory.PreCarm_Trade:
        return this.metadataService.getSoaTradeReasons();
      default:
        return this.metadataService.getSoaSimaReasons();
    }
  }

  public validateSoa(request: ISoaRequest): Observable<ISoaValidationResponse> {
    const endpoint = `${this.baseSoaUrlBP}/validate-lines`;
    return this.httpAuthPost<ISoaValidationResponse>(endpoint, request);
  }

  public submitSoa(request: ISoaRequest): Observable<ISoaSubmitResponse> {
    const endpoint = `${this.baseSoaUrlBP}/submit-appeal`;
    return this.httpAuthPost<ISoaSubmitResponse>(endpoint, request);
  }

  public validatePayment(
    request: ISoaPaymentConfirmationRequest,
  ): Observable<ISoaPaymentConfirmationResponse> {
    const endpoint = `${this.baseSoaUrlBP}/validate-payments`;
    return this.httpAuthPost<ISoaPaymentConfirmationResponse>(
      endpoint,
      request,
    );
  }

  getSoa(caseNumber: string): Observable<ISoaResponse> {
    const endpoint = `${this.baseSoaUrlBP}/view/${caseNumber}`;
    return this.httpAuthGet<ISoaResponse>(endpoint);
  }
}
